import React from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";

export const PricingSection = () => {
  const plans = [
    {
      id: "starter",
      name: "Starter",
      price: "Free",
      isNumericPrice: false,
      subtitle: "Your first video, on us",
      features: [
        "1 free video",
        "All ready-made styles",
        "Standard quality download",
      ],
      ctaText: "Start Free",
      isPopular: false,
    },
    {
      id: "creator",
      name: "Creator",
      price: "₹499",
      period: "/month",
      isNumericPrice: true,
      subtitle: "For regular use, any occasion",
      features: [
        "15 videos every month",
        "HD quality download",
        "Priority processing",
        "Real human support",
      ],
      ctaText: "Choose Creator",
      isPopular: true,
      badge: "★ Most Popular",
    },
    {
      id: "studio",
      name: "Studio",
      price: "₹799",
      period: "/month",
      isNumericPrice: true,
      subtitle: "For shops and event businesses",
      features: [
        "Unlimited videos",
        "Full HD quality download",
        "Add your shop's name & logo",
        "Priority support line",
      ],
      ctaText: "Choose Studio",
      isPopular: false,
    },
  ];

  return (
    <section id="pricing" className="text-white py-24 px-4 sm:px-6 lg:px-8 font-sora relative overflow-hidden bg-[#D9D9D9]/[0.10] backdrop-blur-sm">
      {/* Background Ambient Glows */}
      <div className="absolute top-1/4 -left-28 w-[550px] h-[550px] rounded-full pointer-events-none z-0" style={{ background: "radial-gradient(circle at center, rgba(98,250,227,0.12) 0%, transparent 65%)", filter: "blur(60px)" }} />
      <div className="absolute top-1/3 -right-28 w-[550px] h-[550px] rounded-full pointer-events-none z-0" style={{ background: "radial-gradient(circle at center, rgba(208,188,255,0.14) 0%, transparent 65%)", filter: "blur(60px)" }} />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16 sm:mb-20">
          <p className="text-xs font-semibold tracking-widest text-[#2DD4BF] uppercase mb-3 font-mono">
            SIMPLE PRICING
          </p>
          <h2 className="text-4xl sm:text-5xl lg:text-[56px] font-bold tracking-tight leading-[1.12] text-white mb-5 font-headline">
            Choose the Plan That<br />
            <span className="bg-gradient-to-r from-[#8B5CF6] via-[#A78BFA] to-[#2DD4BF] bg-clip-text text-transparent">
              Fits You
            </span>
          </h2>
          <p className="text-gray-400 text-sm sm:text-base font-normal leading-relaxed max-w-xl mx-auto font-body">
            Scalable solutions for creators, families, and studios. High-end storytelling powered by AI, tailored to your creative workflow.
          </p>
        </div>

        {/* Pricing Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-7 items-stretch max-w-5xl mx-auto font-jakarta">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-[12px] p-7 sm:p-8 flex flex-col transition-all duration-300 ${
                plan.isPopular
                  ? "border border-[#2DD4BF]/50 shadow-[0_0_50px_rgba(45,212,191,0.18)] md:-translate-y-2.5"
                  : "bg-[#101319]/90 border border-white/[0.08] hover:border-white/20 shadow-2xl"
              }`}
              style={
                plan.isPopular
                  ? {
                      background:
                        "linear-gradient(155deg, rgba(60, 40, 105, 0.75) 0%, rgba(20, 32, 50, 0.85) 50%, rgba(14, 45, 52, 0.9) 100%)",
                    }
                  : undefined
              }
            >
              {/* Most Popular Badge */}
              {plan.isPopular && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-3.5 py-1 rounded-full bg-[#D0BCFF] text-[#1e1035] text-[11px] font-bold shadow-lg whitespace-nowrap border border-[#2DD4BF]/40 font-jakarta">
                  {plan.badge}
                </div>
              )}

              <div>
                <h3 className="text-2xl font-bold text-white tracking-tight mb-2.5 font-jakarta">
                  {plan.name}
                </h3>
                <div className="flex items-baseline gap-1 mb-1.5 font-jakarta">
                  <span className="text-4xl sm:text-[42px] font-bold text-white tracking-tight font-jakarta">
                    {plan.price}
                  </span>
                  {plan.period && (
                    <span className="text-xs text-gray-400 font-normal font-jakarta">
                      {plan.period}
                    </span>
                  )}
                </div>
                <p className="text-xs sm:text-sm text-gray-400 font-normal mb-7 font-jakarta">
                  {plan.subtitle}
                </p>

                {/* Features List */}
                <div className="space-y-4.5 font-jakarta">
                  {plan.features.map((feature, fIdx) => (
                    <div key={fIdx} className="flex items-center gap-3 py-0.5 font-jakarta">
                      {plan.isPopular ? (
                        <div className="w-4 h-4 rounded-full bg-[#8B5CF6]/30 border border-[#8B5CF6] text-white flex items-center justify-center shrink-0">
                          <Check className="w-2.5 h-2.5 stroke-[3]" />
                        </div>
                      ) : (
                        <div className="w-4 h-4 rounded-full border border-[#2DD4BF]/80 flex items-center justify-center text-[#2DD4BF] shrink-0">
                          <Check className="w-2.5 h-2.5 stroke-[2.5]" />
                        </div>
                      )}
                      <span
                        className={`text-xs sm:text-sm font-normal font-jakarta ${
                          plan.isPopular ? "text-white" : "text-gray-300"
                        }`}
                      >
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action CTA Button */}
              <div className="mt-7">
                <Link
                  to={`/pricing#plan-${plan.id}`}
                  className={
                    plan.isPopular
                      ? "block text-center w-full py-3.5 rounded-xl bg-gradient-to-r from-[#8B5CF6] via-[#A78BFA] to-[#2DD4BF] text-[#0a0d14] font-bold text-xs tracking-wider transition-all duration-300 hover:brightness-110 shadow-lg shadow-[#2DD4BF]/20 active:scale-95 cursor-pointer font-jakarta"
                      : "block text-center w-full py-3.5 rounded-xl border border-white/15 bg-white/[0.02] hover:bg-white/[0.07] hover:border-white/25 text-white font-semibold text-xs tracking-wider transition-all duration-300 active:scale-95 cursor-pointer font-jakarta"
                  }
                  data-testid={`landing-pricing-cta-${plan.id}`}
                >
                  {plan.ctaText}
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
